import React, { useCallback, useEffect, useState } from 'react';
import { proposalCoreService, Rulebook, ReferenceAccount, ForbiddenTerm, TextCheckHit } from '../services/api';
import { fmtDate, fmtDateTime } from '../utils/formatters';
import CascaDeModal from './shared/CascaDeModal';
import { useConfirm, useConfirmarPerigo } from './shared/ConfirmDialog';
import { campo } from '../design';

/**
 * Núcleo de propostas: o que a geração de proposta lê antes de escrever uma
 * palavra. Três coisas, três abas:
 *  - Regras: o core versionado (uma ativa; correção é versão nova, nunca edição da ativa)
 *  - Provas: quem pode ser citado e com o quê (nome, logo, depoimento, cada um por si)
 *  - Termos proibidos: claims que o gate bloqueia
 * Só ADMIN escreve. Quem monta proposta lê.
 */

type Aba = 'regras' | 'provas' | 'termos';

const INPUT = `w-full px-3 py-2 ${campo} rounded-lg text-sm focus:outline-none placeholder:text-slate-600`;
const BTN_PRIMARY = 'px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white text-sm font-medium rounded-lg';
const BTN = 'px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 text-xs font-medium rounded-lg';
const CARD = 'bg-slate-800/50 border border-slate-700 rounded-xl';

const STATUS_RULEBOOK: Record<string, string> = {
  ativa: 'bg-green-500/15 text-green-400 border-green-500/40',
  rascunho: 'bg-amber-500/15 text-amber-400 border-amber-500/40',
  arquivada: 'bg-slate-500/15 text-slate-400 border-slate-500/40',
};
const STATUS_REF: Record<string, string> = {
  autorizado: 'bg-green-500/15 text-green-400 border-green-500/40',
  bloqueado: 'bg-red-500/15 text-red-400 border-red-500/40',
  pendente: 'bg-amber-500/15 text-amber-400 border-amber-500/40',
};

const Pill = ({ cls, children }: { cls: string; children: React.ReactNode }) => (
  <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full border ${cls}`}>{children}</span>
);

// Moldura dos 4 modais do núcleo: a casca. Editores não fecham ao clicar fora
// (como já era); a leitura de uma versão passa a fechar (`fecharAoClicarFora`).
const Modal = ({ title, onClose, children, footer, wide, fecharAoClicarFora }: { title: string; onClose: () => void; children: React.ReactNode; footer: React.ReactNode; wide?: boolean; fecharAoClicarFora?: boolean }) => (
  <CascaDeModal largura={wide ? 'lg' : 'base'} titulo={title} onClose={onClose} fecharAoClicarFora={fecharAoClicarFora}
    corpoClassName='p-5 space-y-4' rodape={footer}>
    {children}
  </CascaDeModal>
);

const Labeled = ({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) => (
  <div>
    <label className='block text-xs text-slate-400 mb-1'>{label}</label>
    {children}
    {hint && <p className='text-[11px] text-slate-500 mt-1'>{hint}</p>}
  </div>
);

const Check = ({ label, checked, onChange, disabled }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) => (
  <label className={`flex items-center gap-2 text-sm bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 ${disabled ? 'opacity-50' : 'text-slate-300'}`}>
    <input type='checkbox' checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} className='w-4 h-4 rounded bg-slate-800 border-slate-600' />{label}
  </label>
);

export default function NucleoPropostasTab() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user?.role === 'admin';
  const confirmar = useConfirm();
  const confirmarPerigo = useConfirmarPerigo();
  const [aba, setAba] = useState<Aba>('regras');
  const [erro, setErro] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [rulebooks, setRulebooks] = useState<Rulebook[]>([]);
  const [refs, setRefs] = useState<ReferenceAccount[]>([]);
  const [terms, setTerms] = useState<ForbiddenTerm[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setErro(null);
    try {
      const [r, p, t] = await Promise.all([proposalCoreService.listRulebooks(), proposalCoreService.listReferences(), proposalCoreService.listTerms()]);
      setRulebooks(r); setRefs(p); setTerms(t);
    } catch (e: any) { setErro(e?.response?.data?.message || 'Erro ao carregar o núcleo'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<any>) => {
    setSaving(true); setErro(null);
    try { await fn(); await load(); return true; }
    catch (e: any) { const m = e?.response?.data?.message; setErro(Array.isArray(m) ? m.join(' · ') : (m || 'Erro ao salvar')); return false; }
    finally { setSaving(false); }
  };

  // ───────── Regras ─────────
  const [rbModal, setRbModal] = useState<{ mode: 'nova' | 'editar'; id?: number; version: string; content: string; changelog: string } | null>(null);
  const [rbView, setRbView] = useState<Rulebook | null>(null);
  const ativa = rulebooks.find(r => r.status === 'ativa');

  const abrirNova = () => {
    const base = ativa?.version || '1.0';
    const [maj, min] = base.split('.').map(n => parseInt(n) || 0);
    setRbModal({ mode: 'nova', version: `${maj}.${(min || 0) + 1}`, content: ativa?.content || '', changelog: '' });
  };
  const salvarRb = async () => {
    if (!rbModal) return;
    const ok = await run(() => rbModal.mode === 'nova'
      ? proposalCoreService.createRulebook({ version: rbModal.version.trim(), content: rbModal.content, changelog: rbModal.changelog || undefined })
      : proposalCoreService.updateRulebook(rbModal.id!, { content: rbModal.content, changelog: rbModal.changelog || undefined }));
    if (ok) setRbModal(null);
  };

  // ───────── Provas ─────────
  const emptyRef: Partial<ReferenceAccount> = { name: '', segment: '', city: '', status: 'pendente', allowName: false, allowLogo: false, allowTestimonial: false, testimonialText: '', testimonialAuthor: '', isPreviousProposal: false, notes: '', decidedBy: '', decidedAt: '' };
  const [refModal, setRefModal] = useState<{ id?: number; data: Partial<ReferenceAccount> } | null>(null);
  const [refFiltro, setRefFiltro] = useState<'todas' | 'autorizado' | 'bloqueado' | 'pendente' | 'anteriores'>('todas');
  const refsFiltradas = refs.filter(r => refFiltro === 'todas' ? true : refFiltro === 'anteriores' ? r.isPreviousProposal : r.status === refFiltro && !r.isPreviousProposal);
  const salvarRef = async () => {
    if (!refModal) return;
    const d = { ...refModal.data };
    const payload: any = {};
    for (const [k, v] of Object.entries(d)) if (v !== '' && v !== null && v !== undefined) payload[k] = v;
    const ok = await run(() => refModal.id ? proposalCoreService.updateReference(refModal.id, payload) : proposalCoreService.createReference(payload));
    if (ok) setRefModal(null);
  };

  // ───────── Termos ─────────
  const [termModal, setTermModal] = useState<{ id?: number; data: Partial<ForbiddenTerm> } | null>(null);
  const [testeTexto, setTesteTexto] = useState('');
  const [testeCliente, setTesteCliente] = useState('');
  const [testeResultado, setTesteResultado] = useState<{ blocked: boolean; hits: TextCheckHit[] } | null>(null);
  const salvarTerm = async () => {
    if (!termModal) return;
    const ok = await run(() => termModal.id ? proposalCoreService.updateTerm(termModal.id, termModal.data) : proposalCoreService.createTerm(termModal.data));
    if (ok) setTermModal(null);
  };
  const testar = async () => {
    setSaving(true); setErro(null);
    try { setTesteResultado(await proposalCoreService.checkText(testeTexto, testeCliente || undefined)); }
    catch (e: any) { setErro(e?.response?.data?.message || 'Erro ao testar'); }
    finally { setSaving(false); }
  };

  // ⛔ Até 06/10/2026 aqui havia `const confirmar = (msg) => window.confirm(msg)`: o apelido escondia
  // três exclusões de qualquer varredura por `window.confirm`. Ação que não se desfaz usa o diálogo do HUB.

  return (
    <div className='space-y-6'>
      <div className='flex items-start justify-between gap-4 flex-wrap'>
        <div>
          <p className='text-sm text-slate-400 max-w-2xl'>O que a geração de proposta lê antes de escrever: as regras vigentes, quem pode ser citado e o que nunca entra no texto. Cada linha daqui sai impressa numa proposta, por isso só administrador altera.</p>
        </div>
        <div className='flex gap-2'>
          {aba === 'regras' && isAdmin && <button className={BTN_PRIMARY} onClick={abrirNova} disabled={saving}>Nova versão</button>}
          {aba === 'provas' && isAdmin && <button className={BTN_PRIMARY} onClick={() => setRefModal({ data: { ...emptyRef } })} disabled={saving}>Nova prova</button>}
          {aba === 'termos' && isAdmin && <button className={BTN_PRIMARY} onClick={() => setTermModal({ data: { pattern: '', reason: '', severity: 'bloqueia', scope: 'proposta', active: true } })} disabled={saving}>Novo termo</button>}
        </div>
      </div>

      <div className='flex gap-1 border-b border-slate-700'>
        {([['regras', `Regras (${rulebooks.length})`], ['provas', `Provas (${refs.length})`], ['termos', `Termos proibidos (${terms.filter(t => t.active).length})`]] as Array<[Aba, string]>).map(([k, l]) => (
          <button key={k} onClick={() => setAba(k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${aba === k ? 'border-orange-500 text-white' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>{l}</button>
        ))}
      </div>

      {erro && <div className='text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2'>{erro}</div>}
      {loading && <div className='text-sm text-slate-400 py-8 text-center'>Carregando…</div>}

      {/* ───────── Regras ───────── */}
      {!loading && aba === 'regras' && (
        <div className='space-y-3'>
          {!ativa && <div className='text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2'>Nenhuma versão ativa: a geração de proposta fica travada até ativar uma.</div>}
          {rulebooks.map(r => (
            <div key={r.id} className={`${CARD} p-4 ${r.status === 'ativa' ? 'ring-1 ring-green-500/40' : ''}`}>
              <div className='flex items-start justify-between gap-3 flex-wrap'>
                <div className='min-w-0'>
                  <div className='flex items-center gap-2'>
                    <span className='font-mono text-lg font-bold text-white'>v{r.version}</span>
                    <Pill cls={STATUS_RULEBOOK[r.status]}>{r.status}</Pill>
                  </div>
                  <p className='text-xs text-slate-500 mt-1'>
                    {r.effectiveFrom ? `vigência ${fmtDate(r.effectiveFrom)} · ` : ''}atualizado {fmtDateTime(r.updatedAt)} · {Math.round(r.content.length / 1000)} mil caracteres
                  </p>
                  {r.changelog && <p className='text-sm text-slate-300 mt-2 whitespace-pre-wrap'>{r.changelog}</p>}
                </div>
                <div className='flex gap-2 shrink-0'>
                  <button className={BTN} onClick={() => setRbView(r)}>Ler</button>
                  {isAdmin && r.status === 'rascunho' && <button className={BTN} onClick={() => setRbModal({ mode: 'editar', id: r.id, version: r.version, content: r.content, changelog: r.changelog || '' })}>Editar</button>}
                  {isAdmin && r.status === 'rascunho' && <button className={`${BTN} !bg-green-600 hover:!bg-green-500 !text-white`} disabled={saving} onClick={async () => (await confirmar({ title: `Ativar a versão ${r.version}?`, message: `A versão ativa atual (${ativa?.version || 'nenhuma'}) será arquivada e passa a ser imutável.` })) && run(() => proposalCoreService.activateRulebook(r.id))}>Ativar</button>}
                  {isAdmin && r.status === 'rascunho' && <button className={`${BTN} hover:!text-red-400`} disabled={saving} onClick={async () => (await confirmarPerigo('Apagar este rascunho?')) && run(() => proposalCoreService.deleteRulebook(r.id))}>Apagar</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ───────── Provas ───────── */}
      {!loading && aba === 'provas' && (
        <div className='space-y-3'>
          <div className='flex gap-2 flex-wrap'>
            {([['todas', 'Todas'], ['autorizado', 'Autorizadas'], ['pendente', 'Pendentes'], ['bloqueado', 'Bloqueadas'], ['anteriores', 'Já receberam proposta']] as Array<[typeof refFiltro, string]>).map(([k, l]) => (
              <button key={k} onClick={() => setRefFiltro(k)} className={`px-3 py-1.5 text-xs rounded-lg border ${refFiltro === k ? 'bg-orange-500/15 border-orange-500/60 text-orange-300' : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white'}`}>{l}</button>
            ))}
          </div>
          <div className={`${CARD} overflow-x-auto`}>
            <table className='w-full text-sm table-fixed'>
              <thead><tr className='text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-700'>
                <th className='text-left px-4 py-3 w-[34%]'>Cliente</th>
                <th className='text-left px-3 py-3 w-24'>Status</th>
                <th className='text-center px-2 py-3 w-16'>Nome</th>
                <th className='text-center px-2 py-3 w-16'>Logo</th>
                <th className='text-center px-2 py-3 w-24'>Depoimento</th>
                <th className='text-left px-3 py-3'>Decisão</th>
                <th className='px-3 py-3 w-28'></th>
              </tr></thead>
              <tbody>
                {refsFiltradas.map(r => (
                  <tr key={r.id} className='border-b border-slate-700/60 hover:bg-slate-800/40'>
                    <td className='px-4 py-3 min-w-0'>
                      <p className='font-medium text-white truncate'>{r.name}{r.isPreviousProposal && <span className='ml-2 text-[10px] font-mono text-slate-500'>proposta anterior</span>}</p>
                      <p className='text-xs text-slate-500 truncate'>{[r.segment, r.city].filter(Boolean).join(' · ')}</p>
                    </td>
                    <td className='px-3 py-3'><Pill cls={STATUS_REF[r.status]}>{r.status}</Pill></td>
                    <td className='px-2 py-3 text-center'>{r.allowName ? <span className='text-green-400'>✓</span> : <span className='text-slate-600'>✗</span>}</td>
                    <td className='px-2 py-3 text-center'>{r.allowLogo ? <span className='text-green-400'>✓</span> : <span className='text-slate-600'>✗</span>}</td>
                    <td className='px-2 py-3 text-center'>
                      {r.allowTestimonial ? (r.testimonialText ? <span className='text-green-400' title={r.testimonialText}>✓ texto</span> : <span className='text-amber-400' title='Autorizado, mas nenhum texto colhido'>✓ sem texto</span>) : <span className='text-slate-600'>✗</span>}
                    </td>
                    <td className='px-3 py-3 min-w-0'>
                      <p className='text-xs text-slate-300 truncate' title={r.notes || ''}>{r.notes || ''}</p>
                      <p className='text-[11px] text-slate-500'>{[r.decidedBy, r.decidedAt ? fmtDate(r.decidedAt) : ''].filter(Boolean).join(' · ')}</p>
                    </td>
                    <td className='px-3 py-3 text-right whitespace-nowrap'>
                      {isAdmin && <button className={BTN} onClick={() => setRefModal({ id: r.id, data: { ...r, decidedAt: r.decidedAt ? String(r.decidedAt).slice(0, 10) : '' } })}>Editar</button>}
                      {isAdmin && <button className={`${BTN} ml-1 hover:!text-red-400`} onClick={async () => (await confirmarPerigo(`Apagar "${r.name}"? Sem registro, o gate deixa de saber que esse nome não pode vazar.`)) && run(() => proposalCoreService.deleteReference(r.id))}>✕</button>}
                    </td>
                  </tr>
                ))}
                {refsFiltradas.length === 0 && <tr><td colSpan={7} className='px-4 py-6 text-center text-sm text-slate-500'>Nada aqui.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ───────── Termos ───────── */}
      {!loading && aba === 'termos' && (
        <div className='space-y-4'>
          <div className={`${CARD} overflow-x-auto`}>
            <table className='w-full text-sm table-fixed'>
              <thead><tr className='text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-700'>
                <th className='text-left px-4 py-3 w-[30%]'>Expressão</th>
                <th className='text-left px-3 py-3'>Por quê</th>
                <th className='text-left px-3 py-3 w-24'>Severidade</th>
                <th className='text-left px-3 py-3 w-24'>Escopo</th>
                <th className='px-3 py-3 w-28'></th>
              </tr></thead>
              <tbody>
                {terms.map(t => (
                  <tr key={t.id} className={`border-b border-slate-700/60 hover:bg-slate-800/40 ${t.active ? '' : 'opacity-50'}`}>
                    <td className='px-4 py-3 min-w-0'><code className='text-xs text-slate-200 break-all'>{t.pattern}</code>{!t.active && <span className='ml-2 text-[10px] text-slate-500'>inativo</span>}</td>
                    <td className='px-3 py-3 text-xs text-slate-300'>{t.reason}</td>
                    <td className='px-3 py-3'><Pill cls={t.severity === 'bloqueia' ? 'bg-red-500/15 text-red-400 border-red-500/40' : 'bg-amber-500/15 text-amber-400 border-amber-500/40'}>{t.severity}</Pill></td>
                    <td className='px-3 py-3 text-xs text-slate-400'>{t.scope}</td>
                    <td className='px-3 py-3 text-right whitespace-nowrap'>
                      {isAdmin && <button className={BTN} onClick={() => setTermModal({ id: t.id, data: { ...t } })}>Editar</button>}
                      {isAdmin && <button className={`${BTN} ml-1 hover:!text-red-400`} onClick={async () => (await confirmarPerigo('Apagar este termo?')) && run(() => proposalCoreService.deleteTerm(t.id))}>✕</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={`${CARD} p-4 space-y-3`}>
            <div>
              <p className='text-sm font-semibold text-white'>Testar um texto</p>
              <p className='text-xs text-slate-500'>Cole um trecho de proposta. O mesmo verificador que o gate vai usar: termos proibidos e nomes que não podem vazar.</p>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-[1fr_220px] gap-3'>
              <textarea rows={4} value={testeTexto} onChange={e => setTesteTexto(e.target.value)} className={INPUT} placeholder='Texto a conferir' />
              <div className='space-y-2'>
                <input value={testeCliente} onChange={e => setTesteCliente(e.target.value)} className={INPUT} placeholder='Cliente desta peça (opcional)' />
                <button className={`${BTN_PRIMARY} w-full`} onClick={testar} disabled={saving || !testeTexto.trim()}>Conferir</button>
              </div>
            </div>
            {testeResultado && (
              <div className={`rounded-lg border px-3 py-2 text-sm ${testeResultado.blocked ? 'border-red-500/40 bg-red-500/10' : 'border-green-500/40 bg-green-500/10'}`}>
                <p className={`font-medium ${testeResultado.blocked ? 'text-red-400' : 'text-green-400'}`}>{testeResultado.blocked ? `Bloqueado: ${testeResultado.hits.length} ocorrência(s)` : 'Aprovado: nenhum termo ou nome proibido'}</p>
                <ul className='mt-1 space-y-0.5'>
                  {testeResultado.hits.map((h, i) => (
                    <li key={i} className='text-xs text-slate-300'><span className='font-mono text-slate-200'>"{h.match}"</span> <span className='text-slate-500'>({h.kind})</span> · {h.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ───────── Modais ───────── */}
      {rbView && (
        <Modal title={`Núcleo v${rbView.version}`} onClose={() => setRbView(null)} wide fecharAoClicarFora footer={<button className={`${BTN} flex-1 py-2.5`} onClick={() => setRbView(null)}>Fechar</button>}>
          <pre className='whitespace-pre-wrap text-xs text-slate-300 font-mono leading-relaxed'>{rbView.content}</pre>
        </Modal>
      )}

      {rbModal && (
        <Modal title={rbModal.mode === 'nova' ? 'Nova versão do núcleo' : `Editar rascunho v${rbModal.version}`} onClose={() => setRbModal(null)} wide
          footer={<>
            <button className={`${BTN} flex-1 py-2.5`} onClick={() => setRbModal(null)} disabled={saving}>Cancelar</button>
            <button className={`${BTN_PRIMARY} flex-1 py-2.5`} onClick={salvarRb} disabled={saving || !rbModal.version.trim() || rbModal.content.trim().length < 50}>{saving ? 'Salvando…' : 'Salvar rascunho'}</button>
          </>}>
          <p className='text-xs text-slate-500'>Nasce como rascunho. Só vale para a geração depois de ativada, e aí vira imutável: a correção seguinte é outra versão.</p>
          <div className='grid grid-cols-1 md:grid-cols-[160px_1fr] gap-3'>
            <Labeled label='Versão'><input value={rbModal.version} disabled={rbModal.mode === 'editar'} onChange={e => setRbModal({ ...rbModal, version: e.target.value })} className={INPUT} /></Labeled>
            <Labeled label='Changelog (o que mudou e por quê)'><input value={rbModal.changelog} onChange={e => setRbModal({ ...rbModal, changelog: e.target.value })} className={INPUT} placeholder='Ex.: preços de canais ajustados; nova regra de honestidade sobre portabilidade' /></Labeled>
          </div>
          <Labeled label='Conteúdo (markdown)' hint={`${rbModal.content.length.toLocaleString('pt-BR')} caracteres`}>
            <textarea rows={22} value={rbModal.content} onChange={e => setRbModal({ ...rbModal, content: e.target.value })} className={`${INPUT} font-mono text-xs leading-relaxed`} />
          </Labeled>
        </Modal>
      )}

      {refModal && (
        <Modal title={refModal.id ? 'Editar prova' : 'Nova prova'} onClose={() => setRefModal(null)}
          footer={<>
            <button className={`${BTN} flex-1 py-2.5`} onClick={() => setRefModal(null)} disabled={saving}>Cancelar</button>
            <button className={`${BTN_PRIMARY} flex-1 py-2.5`} onClick={salvarRef} disabled={saving || !(refModal.data.name || '').trim()}>{saving ? 'Salvando…' : 'Salvar'}</button>
          </>}>
          {(() => {
            const d = refModal.data; const up = (patch: Partial<ReferenceAccount>) => setRefModal({ ...refModal, data: { ...d, ...patch } });
            const autorizado = d.status === 'autorizado';
            return (<>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
                <Labeled label='Nome'><input value={d.name || ''} onChange={e => up({ name: e.target.value })} className={INPUT} /></Labeled>
                <Labeled label='Segmento'><input value={d.segment || ''} onChange={e => up({ segment: e.target.value })} className={INPUT} /></Labeled>
                <Labeled label='Cidade/UF'><input value={d.city || ''} onChange={e => up({ city: e.target.value })} className={INPUT} /></Labeled>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
                <Labeled label='Status' hint='Bloqueado não autoriza nada, por construção.'>
                  <select value={d.status || 'pendente'} onChange={e => up({ status: e.target.value as any })} className={INPUT}>
                    <option value='autorizado'>Autorizado</option><option value='pendente'>Pendente</option><option value='bloqueado'>Bloqueado</option>
                  </select>
                </Labeled>
                <Labeled label='Decidido por'><input value={d.decidedBy || ''} onChange={e => up({ decidedBy: e.target.value })} className={INPUT} placeholder='Fabio' /></Labeled>
                <Labeled label='Em'><input type='date' value={d.decidedAt || ''} onChange={e => up({ decidedAt: e.target.value })} className={INPUT} /></Labeled>
              </div>
              <div className='grid grid-cols-3 gap-2'>
                <Check label='Nome' checked={!!d.allowName} disabled={!autorizado} onChange={v => up({ allowName: v })} />
                <Check label='Logo' checked={!!d.allowLogo} disabled={!autorizado} onChange={v => up({ allowLogo: v })} />
                <Check label='Depoimento' checked={!!d.allowTestimonial} disabled={!autorizado} onChange={v => up({ allowTestimonial: v })} />
              </div>
              {d.allowTestimonial && autorizado && (
                <div className='space-y-3'>
                  <Labeled label='Texto do depoimento' hint='Autorizado não é o mesmo que existente: sem texto aqui, nada é citado.'><textarea rows={3} value={d.testimonialText || ''} onChange={e => up({ testimonialText: e.target.value })} className={INPUT} /></Labeled>
                  <Labeled label='Quem assina (nome, cargo)'><input value={d.testimonialAuthor || ''} onChange={e => up({ testimonialAuthor: e.target.value })} className={INPUT} /></Labeled>
                </div>
              )}
              <Check label='Já recebeu proposta (o nome não pode aparecer na peça de outro cliente)' checked={!!d.isPreviousProposal} onChange={v => up({ isPreviousProposal: v })} />
              <Labeled label='Como citar, limites, o que nunca afirmar' hint='Vai para a IA junto com o nome.'><textarea rows={3} value={d.notes || ''} onChange={e => up({ notes: e.target.value })} className={INPUT} /></Labeled>
            </>);
          })()}
        </Modal>
      )}

      {termModal && (
        <Modal title={termModal.id ? 'Editar termo proibido' : 'Novo termo proibido'} onClose={() => setTermModal(null)}
          footer={<>
            <button className={`${BTN} flex-1 py-2.5`} onClick={() => setTermModal(null)} disabled={saving}>Cancelar</button>
            <button className={`${BTN_PRIMARY} flex-1 py-2.5`} onClick={salvarTerm} disabled={saving || !(termModal.data.pattern || '').trim() || !(termModal.data.reason || '').trim()}>{saving ? 'Salvando…' : 'Salvar'}</button>
          </>}>
          {(() => {
            const d = termModal.data; const up = (patch: Partial<ForbiddenTerm>) => setTermModal({ ...termModal, data: { ...d, ...patch } });
            return (<>
              <Labeled label='Expressão regular (sem distinguir maiúsculas)' hint='Ex.: tudo inclu[ií]do · \bbarato\b · s[oó] a IPCOM'><input value={d.pattern || ''} onChange={e => up({ pattern: e.target.value })} className={`${INPUT} font-mono`} /></Labeled>
              <Labeled label='Por quê (a forma honesta, quando existe)'><textarea rows={2} value={d.reason || ''} onChange={e => up({ reason: e.target.value })} className={INPUT} /></Labeled>
              <div className='grid grid-cols-3 gap-3'>
                <Labeled label='Severidade'><select value={d.severity || 'bloqueia'} onChange={e => up({ severity: e.target.value as any })} className={INPUT}><option value='bloqueia'>Bloqueia</option><option value='aviso'>Aviso</option></select></Labeled>
                <Labeled label='Escopo'><select value={d.scope || 'proposta'} onChange={e => up({ scope: e.target.value as any })} className={INPUT}><option value='proposta'>Proposta</option><option value='social'>Social</option><option value='ambos'>Ambos</option></select></Labeled>
                <div className='flex items-end'><Check label='Ativo' checked={d.active !== false} onChange={v => up({ active: v })} /></div>
              </div>
            </>);
          })()}
        </Modal>
      )}
    </div>
  );
}
